import type { ConfidenceBand, DataClassification, QualityStatus } from '@aurora-pvi/contracts';
import type { Rng } from './rng';
import type { Provenance } from './types';
import { clamp, round, timestampBefore } from './support';

/**
 * Provenance stamping.
 *
 * Aurora's registered sources. Named for their function rather than their
 * vendor, which is how a carrier's own architects talk about them anyway and
 * which keeps the fixture free of real product names.
 */
export const SOURCE_SYSTEMS = {
  cmdb: 'Aurora CMDB',
  dataHub: 'Aurora Data Hub',
  workTracker: 'Work Tracker',
  serviceDesk: 'Service Desk',
  financeErp: 'Finance ERP',
  hcm: 'HCM Suite',
  procurement: 'Procurement Cloud',
  observability: 'Observability Platform',
  contractManager: 'Contract Manager',
  productRegistry: 'Product Registry',
  manual: 'Manual Upload',
} as const;

export type SourceSystemKey = keyof typeof SOURCE_SYSTEMS;

export function bandFor(confidence: number): ConfidenceBand {
  if (confidence >= 0.85) return 'High';
  if (confidence >= 0.65) return 'Medium';
  if (confidence > 0) return 'Low';
  return 'Unknown';
}

export interface ProvenanceOptions {
  sourceSystem: string;
  sourceRecordId: string;
  /** How many days before `asOf` the source last touched the record. */
  ageDays: number;
  confidence: number;
  qualityStatus?: QualityStatus;
  dataClassification?: DataClassification;
}

export function makeProvenance(asOf: string, options: ProvenanceOptions): Provenance {
  const confidence = round(clamp(options.confidence, 0, 1), 3);
  // A record nobody has refreshed in six weeks is stale whatever its quality
  // flag says. Deriving the status rather than authoring it keeps the freshness
  // badges honest across four thousand records.
  const derivedStatus: QualityStatus =
    options.qualityStatus ??
    (options.ageDays > 42 ? 'Stale' : confidence >= 0.85 ? 'Validated' : 'Unverified');

  return {
    sourceSystem: options.sourceSystem,
    sourceRecordId: options.sourceRecordId,
    sourceUpdatedAt: timestampBefore(asOf, options.ageDays, options.ageDays * 7),
    qualityStatus: derivedStatus,
    confidence,
    confidenceBand: bandFor(confidence),
    dataClassification: options.dataClassification ?? 'Internal',
  };
}

/** Convenience for the bulk generators: jittered age and confidence per record. */
export function provenanceFactory(asOf: string, rng: Rng) {
  return (
    sourceSystem: string,
    sourceRecordId: string,
    options: {
      meanAgeDays?: number;
      meanConfidence?: number;
      qualityStatus?: QualityStatus;
      dataClassification?: DataClassification;
    } = {},
  ): Provenance =>
    makeProvenance(asOf, {
      sourceSystem,
      sourceRecordId,
      ageDays: Math.round(rng.around(options.meanAgeDays ?? 6, 8, 0, 120)),
      confidence: rng.around(options.meanConfidence ?? 0.82, 0.14, 0.25, 0.99),
      ...(options.qualityStatus ? { qualityStatus: options.qualityStatus } : {}),
      ...(options.dataClassification ? { dataClassification: options.dataClassification } : {}),
    });
}
